import type { FileAction } from '@/components/chat/CodeBlockWithHeader';

export type MessageSegment =
  | { type: 'text'; content: string }
  | { type: 'thinking'; content: string; isComplete: boolean }
  | { type: 'code'; language: string; content: string }
  | { type: 'codeWithAction'; action: FileAction; filePath: string; language: string; content: string };

/**
 * Parse message content into segments for rendering.
 * Supports:
 * - <thinking>...</thinking> blocks for AI reasoning
 * - ```lang:action:path code blocks for file operations
 * - Regular ```lang code blocks
 * - Plain text
 */
export function parseMessageContent(content: string): MessageSegment[] {
  const segments: MessageSegment[] = [];

  // First, extract thinking blocks
  const thinkingPattern = /<thinking>([\s\S]*?)<\/thinking>/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  // Collect all thinking block positions
  const thinkingBlocks: { start: number; end: number; content: string }[] = [];
  while ((match = thinkingPattern.exec(content)) !== null) {
    thinkingBlocks.push({
      start: match.index,
      end: match.index + match[0].length,
      content: match[1].trim(),
    });
  }

  // Process content with thinking blocks removed and code blocks parsed
  let processedContent = content;
  let offset = 0;

  for (const block of thinkingBlocks) {
    // Add text before thinking block
    const textBefore = processedContent.substring(offset, block.start - offset + offset);
    if (textBefore.trim()) {
      parseCodeAndText(textBefore, segments);
    }

    // Add thinking block
    segments.push({
      type: 'thinking',
      content: block.content,
      isComplete: true,
    });

    offset = block.end;
  }

  // Add remaining content after last thinking block
  const remaining = processedContent.substring(offset);
  if (remaining.trim()) {
    parseCodeAndText(remaining, segments);
  }

  // If no thinking blocks were found, parse the whole content
  if (thinkingBlocks.length === 0) {
    parseCodeAndText(content, segments);
  }

  return segments;
}

/**
 * Parse code blocks and text from content.
 * Supports enhanced code blocks with format: ```lang:action:filepath
 */
function parseCodeAndText(content: string, segments: MessageSegment[]): void {
  // Pattern for code blocks - supports both regular and enhanced formats
  // Enhanced format: ```lang:action:filepath
  // Regular format: ```lang
  const codeBlockPattern = /```(\w+)?(?::(\w+):([^\n]+))?\n?([\s\S]*?)```/g;

  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = codeBlockPattern.exec(content)) !== null) {
    // Add text before code block
    const textBefore = content.substring(lastIndex, match.index);
    if (textBefore.trim()) {
      segments.push({ type: 'text', content: textBefore });
    }

    const [, language = 'text', action, filePath, code] = match;

    if (action && filePath && isValidFileAction(action)) {
      // Enhanced code block with file action
      segments.push({
        type: 'codeWithAction',
        action: action as FileAction,
        filePath: filePath.trim(),
        language,
        content: code.trim(),
      });
    } else {
      // Regular code block
      segments.push({
        type: 'code',
        language,
        content: code.trim(),
      });
    }

    lastIndex = match.index + match[0].length;
  }

  // Add remaining text
  const remaining = content.substring(lastIndex);
  if (remaining.trim()) {
    segments.push({ type: 'text', content: remaining });
  }
}

function isValidFileAction(action: string): action is FileAction {
  return ['reading', 'editing', 'creating', 'deleting'].includes(action);
}

/**
 * Check if content has any thinking blocks.
 */
export function hasThinkingContent(content: string): boolean {
  return /<thinking>[\s\S]*?<\/thinking>/.test(content);
}

/**
 * Strip thinking blocks from content (for display when thinking is hidden).
 */
export function stripThinkingContent(content: string): string {
  return content.replace(/<thinking>[\s\S]*?<\/thinking>/g, '').trim();
}
